import React, { useState, useContext } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import VisualPickerModal from './VisualPickerModal';
import { APIAddEditPresentation } from '../helper/APIFunctions';
import { UserInfo, VisualCache } from '../helper/Context';
import './ContentCreationWizard.css'; // reuse quiz builder CSS classes

/**
 * QuizEditModal — opens when a user edits an existing is_quiz presentation.
 * Pre-populates from the first slide of the presentation, saves via
 * APIAddEditPresentation (PUT) with is_quiz: true, and calls onSaved(updatedPresentation)
 * on success.
 */
const QuizEditModal = ({ show, onHide, presentation, onSaved, isOrg = false }) => {
  const { userInfo } = useContext(UserInfo);
  const { visCache } = useContext(VisualCache);

  // Pull initial values from the existing presentation's first slide.
  // normalizePresentation spreads the visual object onto the slide, so we
  // reconstruct the visual from visCache using visual_id.
  const slide = presentation?.slides?.[0] || {};
  const initialVisual = (slide.visual_id && visCache?.[slide.visual_id]) || null;

  const [quizTitle, setQuizTitle]           = useState(presentation?.metadata?.title || presentation?.title || '');
  const [quizQuestion, setQuizQuestion]     = useState(slide.question || '');
  const [quizAnswers, setQuizAnswers]       = useState(
    slide.answers?.length > 0
      ? slide.answers
      : [
          { id: `ans-${Date.now()}-0`, text: '' },
          { id: `ans-${Date.now()}-1`, text: '' },
        ]
  );
  const [quizCorrectIndex, setQuizCorrectIndex] = useState(slide.correctAnswerIndex ?? 0);
  const [quizVisual, setQuizVisual]         = useState(initialVisual);
  const [showVisualPicker, setShowVisualPicker] = useState(false);
  const [isSaving, setIsSaving]             = useState(false);
  const [saveError, setSaveError]           = useState(null);

  const buildPayload = () => ({
    id: presentation.id,
    title: quizTitle.trim() || 'Untitled Quiz',
    description: quizQuestion.trim(),
    is_presentation: true,
    is_quiz: true,
    metadata: {
      title: quizTitle.trim() || 'Untitled Quiz',
      abstract: quizQuestion.trim(),
      author: userInfo?.display_name || '',
      date: new Date().toISOString().slice(0, 10),
    },
    slides: [
      {
        type: 'quiz',
        visual_id: quizVisual ? quizVisual.id : undefined,
        header: quizTitle.trim() || undefined,
        question: quizQuestion.trim(),
        answers: quizAnswers,
        correctAnswerIndex: quizCorrectIndex,
      },
    ],
    privacy: presentation.privacy || 'unlisted',
  });

  const handleSave = () => {
    if (!quizQuestion.trim()) {
      setSaveError('Please enter a quiz question.');
      return;
    }
    if (quizAnswers.some(a => !a.text.trim())) {
      setSaveError('Please fill in all answer options.');
      return;
    }
    setSaveError(null);
    setIsSaving(true);

    const payload = buildPayload();
    console.log('[QuizEditModal] Saving quiz payload:', JSON.stringify(payload, null, 2));

    APIAddEditPresentation(
      payload,
      (savedPresentation) => {
        console.log('[QuizEditModal] Quiz saved successfully:', JSON.stringify(savedPresentation, null, 2));
        setIsSaving(false);
        if (onSaved) onSaved(savedPresentation);
        onHide();
      },
      (err) => {
        console.error('[QuizEditModal] Quiz save failed:', err);
        setIsSaving(false);
        setSaveError(err?.message || 'Failed to save. Please try again.');
      },
      isOrg,
      'scrollytelling'
    );
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Quiz</Modal.Title>
        </Modal.Header>

        <Modal.Body className="wizard-body" style={{ maxHeight: '72vh' }}>
          <div className="wizard-step">
            <div className="quiz-builder-section">

              {/* Title */}
              <Form.Group className="mb-3">
                <Form.Label>Quiz Title <span className="text-muted fw-normal">(optional)</span></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Test Your Knowledge!"
                  value={quizTitle}
                  onChange={e => setQuizTitle(e.target.value)}
                />
              </Form.Group>

              {/* Question */}
              <Form.Group className="mb-3">
                <Form.Label>Question <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter your quiz question…"
                  value={quizQuestion}
                  onChange={e => setQuizQuestion(e.target.value)}
                />
              </Form.Group>

              {/* Answer options */}
              <Form.Group className="mb-3">
                <Form.Label>Answer Options <span className="text-danger">*</span></Form.Label>
                <p className="text-muted small mb-2">Select the radio button next to the correct answer.</p>
                {quizAnswers.map((answer, idx) => (
                  <div key={answer.id} className="quiz-answer-row mb-2">
                    <Form.Check
                      type="radio"
                      name="quiz-edit-correct"
                      checked={quizCorrectIndex === idx}
                      onChange={() => setQuizCorrectIndex(idx)}
                      className="quiz-answer-radio"
                      title="Mark as correct answer"
                      label=""
                    />
                    <Form.Control
                      type="text"
                      placeholder={`Answer ${idx + 1}`}
                      value={answer.text}
                      onChange={e => {
                        const updated = quizAnswers.map((a, i) =>
                          i === idx ? { ...a, text: e.target.value } : a
                        );
                        setQuizAnswers(updated);
                      }}
                    />
                    {quizAnswers.length > 2 && (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                          const updated = quizAnswers.filter((_, i) => i !== idx);
                          if (quizCorrectIndex >= updated.length)
                            setQuizCorrectIndex(updated.length - 1);
                          setQuizAnswers(updated);
                        }}
                      >
                        ×
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-1"
                  onClick={() =>
                    setQuizAnswers([
                      ...quizAnswers,
                      { id: `ans-${Date.now()}-${quizAnswers.length}`, text: '' },
                    ])
                  }
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Answer
                </Button>
              </Form.Group>

              {/* Optional visual */}
              <Form.Group className="mb-4">
                <Form.Label>Background Visual <span className="text-muted fw-normal">(optional)</span></Form.Label>
                <p className="text-muted small mb-2">
                  Select a visual from your library to display behind the quiz.
                </p>
                {quizVisual ? (
                  <div className="d-flex align-items-center gap-3 p-2 border rounded">
                    <img
                      src={quizVisual.thumbnail || 'https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg'}
                      alt={quizVisual.title}
                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }}
                    />
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{quizVisual.title}</div>
                      <div className="text-muted small">{quizVisual.description || 'No description'}</div>
                    </div>
                    <div className="d-flex flex-column gap-1">
                      <Button variant="outline-primary" size="sm" onClick={() => setShowVisualPicker(true)}>
                        Change
                      </Button>
                      <Button variant="outline-secondary" size="sm" onClick={() => setQuizVisual(null)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button variant="outline-primary" onClick={() => setShowVisualPicker(true)}>
                    Select Visual
                  </Button>
                )}
              </Form.Group>

              {saveError && (
                <div className="alert alert-danger py-2">{saveError}</div>
              )}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving || !quizQuestion.trim()}
            >
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                'Save Quiz'
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Visual picker */}
      {showVisualPicker && (
        <VisualPickerModal
          show={showVisualPicker}
          onHide={() => setShowVisualPicker(false)}
          userInfo={userInfo}
          onSelect={(vis) => {
            setQuizVisual(vis);
            setShowVisualPicker(false);
          }}
        />
      )}
    </>
  );
};

export default QuizEditModal;
